import React, { useContext, useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { FaArrowRight } from 'react-icons/fa6';
import { AiSparkleIcon } from '@/assets/AiSparkleIcon';
import {
  AudienceContext,
  View,
} from '@/features/editor/widgets/shared/modals/audience/audience-context';
import { useAudienceBuilderApi } from '@/features/dashboard/audiences/use-audience-builder-api';

export function AiAudienceInput() {
  const { audienceProps, onQueryBuilderChange, view, setAiPrompt } =
    useContext(AudienceContext);
  const { translatePrompt, loading, abort } = useAudienceBuilderApi();
  const [prompt, setPrompt] = useState(``);
  const inputRef = useRef<HTMLInputElement>(null);
  const loadingRef = useRef(loading);
  loadingRef.current = loading;

  useEffect(
    () => () => {
      if (loadingRef.current) {
        abort();
      }
    },
    [],
  );

  useEffect(() => {
    if (view !== View.SELECT && loading) {
      setAiPrompt(``);
      abort();
    }
  }, [view, loading, abort, setAiPrompt]);

  const filterValidItems = (items: any[]) => {
    const validCaptions = new Set(audienceProps.map((p) => p.caption));
    return items.filter((item) => validCaptions.has(item?.qbProps?.caption));
  };

  const openCustomViewWithPrefill = (filtered: any[]) => {
    if (filtered.length > 0) {
      onQueryBuilderChange(filtered);
      setAiPrompt(prompt);
    } else {
      setAiPrompt(``);
      onQueryBuilderChange([]);
    }
  };

  const handleSend = async () => {
    if (!prompt.trim() || loading) return;
    try {
      const result = await translatePrompt(prompt);
      const filtered = filterValidItems(Array.isArray(result) ? result : []);
      openCustomViewWithPrefill(filtered);
    } catch (e: any) {
      setAiPrompt(``);
      if (e?.name === `AbortError`) return;
      onQueryBuilderChange([]);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === `Enter` && !isSendDisabled) {
      e.preventDefault();
      handleSend();
    }
  };

  const isSendDisabled = !prompt.trim() || loading;

  return (
    <Wrapper>
      <Header>
        <AiSparkleIcon size={15} />
        <HeaderText>Describe your audience</HeaderText>
      </Header>
      <InputRow>
        <StyledInput
          ref={inputRef}
          type="text"
          maxLength={500}
          disabled={loading}
          aria-label="Describe your audience"
          placeholder={`e.g., Users who visited a product page in the last 7 days but didn't make a purchase`}
          value={prompt}
          onChange={(e) => setPrompt(e.target.value)}
          onKeyDown={handleKeyDown}
        />
        <SendButton
          type="button"
          disabled={isSendDisabled}
          aria-label="Generate audience rules from description"
          onClick={handleSend}
        >
          {loading ? <ButtonSpinner /> : <FaArrowRight size={14} />}
        </SendButton>
      </InputRow>
    </Wrapper>
  );
}

const Wrapper = styled.div`
  width: 100%;
  background: #f4f6f6;
  border-radius: 10px;
  padding: 1.5rem 1.5rem;
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  gap: 0.6rem;
  color: #354452;
  font-family: 'Inter', sans-serif;
  font-weight: 600;
  font-size: 1.3rem;
`;

const HeaderText = styled.span`
  font-family: 'Inter', sans-serif;
  font-weight: 600;
  font-size: 1.3rem;
  color: #354452;
`;

const InputRow = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  width: 100%;
`;

const StyledInput = styled.input`
  width: 100%;
  height: 4.5rem;
  border: none;
  border-radius: 15px;
  padding: 0 5rem 0 1.5rem;
  font-family: 'Inter', sans-serif;
  font-size: 1.3rem;
  color: #354452;
  background: #ffffff;
  box-shadow: 0 2px 8px 0 rgba(0, 0, 0, 0.08);
  outline: none;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;

  &::placeholder {
    color: #a6afb8;
    font-size: 1.2rem;
  }

  &:focus {
    box-shadow: 0 2px 10px 0 rgba(0, 0, 0, 0.22);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const SendButton = styled.button`
  position: absolute;
  right: 1.3rem;
  width: 2.5rem;
  height: 2.5rem;
  border-radius: 50%;
  background: #111111;
  border: none;
  color: #ffffff;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  flex-shrink: 0;
  transition: opacity 0.15s ease;

  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }

  &:hover:not(:disabled) {
    opacity: 0.75;
  }
`;

const ButtonSpinner = styled.span`
  display: inline-block;
  width: 1.4rem;
  height: 1.4rem;
  border: 2px solid rgba(255, 255, 255, 0.4);
  border-top-color: #ffffff;
  border-radius: 50%;
  animation: ai-input-spin 0.7s linear infinite;

  @keyframes ai-input-spin {
    to {
      transform: rotate(360deg);
    }
  }
`;
